/**
 * Bundled demo dataset.
 *
 * A faithful mirror of supabase/seed.sql - same UUIDs, same names, same
 * relative dates - so the screens can be demonstrated to Michel before a
 * Supabase project exists, and so switching to a real backend changes nothing
 * visible. Every person here is obviously fake on purpose. The real roster is
 * still pending (departing coaches).
 *
 * Two things this file is careful about:
 *
 *  - It is a FACTORY, not a set of module-level arrays. createSeedData() can be
 *    called again at any time, which is what "reset demo" does, and it rebuilds
 *    the relative dates from the current clock so the story never goes stale.
 *  - Derived rows are DERIVED. Earnings run through the same pay math as the
 *    SQL (mock/pay.ts) and the notification queue runs through the same enqueue
 *    contract as the triggers (mock/notify.ts), instead of being typed out by
 *    hand. That is what keeps the demo honest when a write lands.
 *
 * The live, mutable copy lives in demoDb and is owned by mock/store.ts.
 *
 * Known deliberate divergences from seed.sql, all in service of the demo story:
 *  - Only two post-event reports are seeded, not nine, so several worked events
 *    are still waiting for their report.
 *  - Youssef Stub has module_4 recorded (manual_admin, the way Omar and Rania
 *    have it) so his pending Camp request is genuinely eligible. That is the
 *    approve-and-watch-camp-events-appear beat.
 *  - Availability and assignment rows differ in a few places; the shape and the
 *    story are the same.
 */

import type {
  Assignment,
  Availability,
  ChecklistItem,
  Earning,
  EventRow,
  MaterialsCheck,
  ModuleCompletion,
  NotificationQueueRow,
  PayRate,
  PostEventReport,
  Profile,
  PushSubscriptionRow,
  Qualification,
  Sanction,
  Service,
  ServiceModuleRequirement,
  TrainingModule,
} from '../../types/db'
import { calculateEventPay } from './pay'
import {
  buildAssignmentNotifications,
  buildAvailabilityRequestNotification,
  buildEventCancelledNotification,
  buildQualificationApprovedNotification,
  type NewNotification,
} from './notify'

/* -------------------------------------------------------------------------- */
/* Date helpers                                                               */
/* -------------------------------------------------------------------------- */

const DAY_MS = 86_400_000
const HOUR_MS = 3_600_000

function dayOffset(days: number): string {
  return new Date(Date.now() + days * DAY_MS).toISOString().slice(0, 10)
}

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * DAY_MS).toISOString()
}

function hoursFromIso(iso: string, hours: number): string {
  return new Date(new Date(iso).getTime() + hours * HOUR_MS).toISOString()
}

/** Build a UTC timestamp from a Dubai wall-clock date and time (UTC+4, no DST). */
function dubaiIso(date: string, time: string): string {
  return new Date(`${date}T${time}:00+04:00`).toISOString()
}

/**
 * First day of the Dubai payroll month, matching
 * date_trunc('month', starts_at at time zone 'Asia/Dubai') in SQL. Doing this
 * in UTC would put a 00:30 Dubai event in the previous month.
 */
function dubaiPeriodMonth(iso: string): string {
  return new Date(new Date(iso).getTime() + 4 * HOUR_MS).toISOString().slice(0, 8) + '01'
}

/* -------------------------------------------------------------------------- */
/* Identifiers, matching seed.sql                                             */
/* -------------------------------------------------------------------------- */

export const IDS = {
  michel: 'aaaaaaaa-0000-4000-8000-000000000001',
  gina: 'aaaaaaaa-0000-4000-8000-000000000002',
  pascal: 'aaaaaaaa-0000-4000-8000-000000000003',
  layla: 'cccccccc-0000-4000-8000-000000000001',
  omar: 'cccccccc-0000-4000-8000-000000000002',
  nadia: 'cccccccc-0000-4000-8000-000000000003',
  karim: 'cccccccc-0000-4000-8000-000000000004',
  rania: 'cccccccc-0000-4000-8000-000000000005',
  youssef: 'cccccccc-0000-4000-8000-000000000006',
  hana: 'cccccccc-0000-4000-8000-000000000007',
  tariq: 'cccccccc-0000-4000-8000-000000000008',
} as const

export const SERVICE_IDS = {
  birthday: 'b0b0b0b0-0000-4000-8000-000000000001',
  brunch: 'b0b0b0b0-0000-4000-8000-000000000002',
  camp: 'b0b0b0b0-0000-4000-8000-000000000003',
  corporate: 'b0b0b0b0-0000-4000-8000-000000000004',
  swimming: 'b0b0b0b0-0000-4000-8000-000000000005',
  cdp: 'b0b0b0b0-0000-4000-8000-000000000006',
} as const

export const MODULE_IDS = {
  m1: 'd0d0d0d0-0000-4000-8000-000000000001',
  m2: 'd0d0d0d0-0000-4000-8000-000000000002',
  m3: 'd0d0d0d0-0000-4000-8000-000000000003',
  m4: 'd0d0d0d0-0000-4000-8000-000000000004',
} as const

const SVC = SERVICE_IDS
const MOD = MODULE_IDS

/** Event uuid by seed number, e0e0e0e0-...-000000000001 upwards. */
export const eventId = (n: number): string =>
  `e0e0e0e0-0000-4000-8000-${String(n).padStart(12, '0')}`

const EV = eventId

/** Every table the demo data source serves. */
export interface DemoTables {
  services: Service[]
  trainingModules: TrainingModule[]
  serviceModuleRequirements: ServiceModuleRequirement[]
  profiles: Profile[]
  moduleCompletions: ModuleCompletion[]
  qualifications: Qualification[]
  events: EventRow[]
  availabilities: Availability[]
  assignments: Assignment[]
  materialsChecks: MaterialsCheck[]
  checklistItems: ChecklistItem[]
  postEventReports: PostEventReport[]
  payRates: PayRate[]
  earnings: Earning[]
  sanctions: Sanction[]
  notificationQueue: NotificationQueueRow[]
  pushSubscriptions: PushSubscriptionRow[]
}

/* -------------------------------------------------------------------------- */
/* The seed                                                                   */
/* -------------------------------------------------------------------------- */

// eslint-disable-next-line max-lines-per-function
export function createSeedData(): DemoTables {
  const NOW = new Date().toISOString()
  const stamps = { created_at: daysAgoIso(400), updated_at: NOW }

  /* ---------------------------------------------------------------- services */

  const services: Service[] = [
    {
      id: SVC.birthday,
      code: 'birthday',
      name: 'Birthday Parties',
      description: 'Kids birthday parties at villas, homes and venues.',
      default_duration_minutes: 120,
      color_hex: '#F5A623',
      sort_order: 1,
      is_active: true,
      ...stamps,
    },
    {
      id: SVC.brunch,
      code: 'brunch',
      name: 'Brunch',
      description: 'Kids club and activity corner at hotel brunches.',
      default_duration_minutes: 180,
      color_hex: '#7ED321',
      sort_order: 2,
      is_active: true,
      ...stamps,
    },
    {
      id: SVC.camp,
      code: 'camp',
      name: 'Camp',
      description: 'Multi-hour sports and activity camps.',
      default_duration_minutes: 240,
      color_hex: '#4A90D9',
      sort_order: 3,
      is_active: true,
      ...stamps,
    },
    {
      id: SVC.corporate,
      code: 'corporate',
      name: 'Corporate Events',
      description: 'Team building and family days for companies.',
      default_duration_minutes: 180,
      color_hex: '#9013FE',
      sort_order: 4,
      is_active: true,
      ...stamps,
    },
    {
      id: SVC.swimming,
      code: 'swimming',
      name: 'Swimming',
      description: 'Pool sessions and swimming lessons.',
      default_duration_minutes: 60,
      color_hex: '#50E3C2',
      sort_order: 5,
      is_active: true,
      ...stamps,
    },
    {
      id: SVC.cdp,
      code: 'cdp',
      name: 'Child Development Program',
      description: 'One to one developmental sessions, logged per child.',
      default_duration_minutes: 60,
      color_hex: '#D0021B',
      sort_order: 6,
      is_active: true,
      ...stamps,
    },
  ]

  /* -------------------------------------------------------- training modules */

  const trainingModules: TrainingModule[] = [
    {
      id: MOD.m1,
      code: 'module_1',
      title: 'Fundamentals',
      summary: 'How La Folie runs an event: arrival, setup, energy, safety, wrap up.',
      content_md:
        '## Arrival\nBe on site 30 minutes before the start time. Introduce yourself to the client by name.\n\n## Setup\nUnpack the materials crate against the checklist before any child arrives.\n\n## Energy\nOpen loud, settle in the middle, finish calm. Never end on a high energy game.\n\n## Safety\nCount the children at the start, after every transition, and at the end.\n\n## Wrap up\nRepack against the checklist, photograph the space, thank the client.',
      content_url: null,
      estimated_minutes: 45,
      sort_order: 1,
      is_published: true,
      ...stamps,
    },
    {
      id: MOD.m2,
      code: 'module_2',
      title: 'Child Psychology and First Aid',
      summary: 'Age-appropriate handling, de-escalation, and first aid basics.',
      content_md:
        '## Age bands\nWhat works at 4 does not work at 9. Match the game to the band.\n\n## De-escalation\nLower your voice, get to eye level, offer two acceptable choices.\n\n## First aid basics\nKnow where the kit is before you need it. Report every incident, however small.',
      content_url: null,
      estimated_minutes: 60,
      sort_order: 2,
      is_published: true,
      ...stamps,
    },
    {
      id: MOD.m3,
      code: 'module_3',
      title: 'Child Development Program',
      summary: 'CDP session structure, activity selection and observation notes.',
      content_md:
        '## Session structure\nWarm up, focus activity, free choice, review.\n\n## Choosing activities\nPick from the band the child finished last session, not the one they struggled with.\n\n## Observation notes\nOne sentence. What the child did, not what you felt about it.',
      content_url: null,
      estimated_minutes: 75,
      sort_order: 3,
      is_published: true,
      ...stamps,
    },
    {
      // Ships unpublished: the content is on Drive and has not been extracted.
      // It still gates Camp, which is why Camp approval stays a human decision.
      id: MOD.m4,
      code: 'module_4',
      title: 'Camp Behavioural and Medical',
      summary:
        'Camp-specific behaviour management and medical protocols. Content is still being prepared.',
      content_md: null,
      content_url: null,
      estimated_minutes: 90,
      sort_order: 4,
      is_published: false,
      ...stamps,
    },
  ]

  const requirementPairs: Array<[keyof typeof SVC, keyof typeof MOD]> = [
    ['birthday', 'm1'],
    ['brunch', 'm1'],
    ['swimming', 'm1'],
    ['swimming', 'm2'],
    ['corporate', 'm1'],
    ['corporate', 'm2'],
    ['camp', 'm1'],
    ['camp', 'm2'],
    ['camp', 'm4'],
    ['cdp', 'm1'],
    ['cdp', 'm2'],
    ['cdp', 'm3'],
  ]

  const serviceModuleRequirements: ServiceModuleRequirement[] = requirementPairs.map(
    ([svc, mod], i) => ({
      id: `f0f0f0f0-0000-4000-8000-${String(i + 1).padStart(12, '0')}`,
      service_id: SVC[svc],
      module_id: MOD[mod],
      is_mandatory: true,
      created_at: daysAgoIso(400),
    }),
  )

  /* ---------------------------------------------------------------- profiles */

  function profile(
    id: string,
    role: Profile['role'],
    full_name: string,
    phone: string,
    status: Profile['status'],
    seniority: Profile['seniority'],
    contract_signed: boolean,
    joinDaysAgo: number,
    is_delegated_approver: boolean,
    notes: string,
  ): Profile {
    return {
      id,
      role,
      full_name,
      phone,
      email: `${phone.slice(1)}@staff.lafolie.internal`,
      status,
      seniority,
      contract_signed,
      contract_signed_at: contract_signed ? daysAgoIso(joinDaysAgo - 20) : null,
      join_date: dayOffset(-joinDaysAgo),
      is_delegated_approver,
      avatar_url: null,
      notes,
      created_at: daysAgoIso(joinDaysAgo),
      updated_at: NOW,
    }
  }

  const profiles: Profile[] = [
    profile(IDS.michel, 'admin', 'Michel Mock', '+971501230100', 'active', 'lead', true, 900, false, 'DEMO DATA. CEO and Head Coach. Sole approver and assigner.'),
    profile(IDS.gina, 'materials', 'Gina Sample', '+971501230101', 'active', 'standard', true, 850, false, 'DEMO DATA. Materials manager: check-out and check-in.'),
    profile(IDS.pascal, 'finance', 'Pascal Demo', '+971501230102', 'active', 'standard', true, 850, false, 'DEMO DATA. Admin and accountant: payroll, penalties, expenses.'),
    profile(IDS.layla, 'coach', 'Layla Mock', '+971501230001', 'active', 'senior', true, 700, false, 'DEMO DATA.'),
    profile(IDS.omar, 'coach', 'Omar Sample', '+971501230002', 'active', 'standard', true, 620, false, 'DEMO DATA.'),
    profile(IDS.nadia, 'coach', 'Nadia Demo', '+971501230003', 'active', 'standard', false, 400, false, 'DEMO DATA. Contract not signed, so no penalty may be applied.'),
    profile(IDS.karim, 'coach', 'Karim Fixture', '+971501230004', 'active', 'junior', false, 120, false, 'DEMO DATA. Newest coach, birthdays only.'),
    profile(IDS.rania, 'coach', 'Rania Sandbox', '+971501230005', 'active', 'lead', true, 1000, true, 'DEMO DATA. Backup approver, covers the single point of failure on Michel.'),
    profile(IDS.youssef, 'coach', 'Youssef Stub', '+971501230006', 'active', 'standard', false, 300, false, 'DEMO DATA. Camp modules done, waiting on the approval call.'),
    profile(IDS.hana, 'coach', 'Hana Seed', '+971501230007', 'candidate', 'junior', false, 10, false, 'DEMO DATA. Self-service candidate, mid training, no approvals yet.'),
    profile(IDS.tariq, 'coach', 'Tariq Dummy', '+971501230008', 'departed', 'standard', true, 1100, false, 'DEMO DATA. Left the roster, qualifications revoked.'),
  ]

  /* ------------------------------------------------------- module completions */

  let completionSeq = 0
  function completion(
    coach_id: string,
    module_id: string,
    daysAgo: number,
    score: number,
    source: ModuleCompletion['source'],
  ): ModuleCompletion {
    completionSeq += 1
    return {
      id: `11110000-0000-4000-8000-${String(completionSeq).padStart(12, '0')}`,
      coach_id,
      module_id,
      completed_at: daysAgoIso(daysAgo),
      score,
      source,
      recorded_by: source === 'manual_admin' ? IDS.michel : null,
      created_at: daysAgoIso(daysAgo),
    }
  }

  const moduleCompletions: ModuleCompletion[] = [
    completion(IDS.layla, MOD.m1, 210, 92, 'manual_admin'),
    completion(IDS.layla, MOD.m2, 205, 88, 'manual_admin'),
    completion(IDS.omar, MOD.m1, 180, 85, 'manual_admin'),
    completion(IDS.omar, MOD.m2, 175, 79.5, 'manual_admin'),
    completion(IDS.omar, MOD.m4, 100, 81, 'manual_admin'),
    completion(IDS.nadia, MOD.m1, 150, 90, 'manual_admin'),
    completion(IDS.nadia, MOD.m2, 140, 86, 'manual_admin'),
    completion(IDS.karim, MOD.m1, 95, 76, 'in_app'),
    completion(IDS.rania, MOD.m1, 320, 97, 'manual_admin'),
    completion(IDS.rania, MOD.m2, 315, 95, 'manual_admin'),
    completion(IDS.rania, MOD.m3, 200, 93, 'manual_admin'),
    completion(IDS.rania, MOD.m4, 90, 91, 'manual_admin'),
    completion(IDS.youssef, MOD.m1, 220, 83, 'manual_admin'),
    completion(IDS.youssef, MOD.m2, 60, 80, 'in_app'),
    // Module 4 is unpublished, so it can only be recorded by Michel from
    // training that happened before the portal existed. Same as Omar and Rania.
    completion(IDS.youssef, MOD.m4, 40, 78, 'manual_admin'),
    completion(IDS.hana, MOD.m1, 4, 88, 'in_app'),
    completion(IDS.tariq, MOD.m1, 500, 84, 'manual_admin'),
    completion(IDS.tariq, MOD.m2, 495, 82, 'manual_admin'),
    completion(IDS.tariq, MOD.m3, 300, 87, 'manual_admin'),
  ]

  /* ----------------------------------------------------------- qualifications */

  let qualSeq = 0
  function qualification(
    coach_id: string,
    service_id: string,
    status: Qualification['status'],
    daysAgo: number,
    notes: string | null = null,
  ): Qualification {
    qualSeq += 1
    const approved = status === 'approved'
    const everApproved = approved || status === 'revoked'
    return {
      id: `22220000-0000-4000-8000-${String(qualSeq).padStart(12, '0')}`,
      coach_id,
      service_id,
      status,
      requested_at: daysAgoIso(daysAgo + 5),
      approved_by: everApproved ? IDS.michel : null,
      approved_at: everApproved ? daysAgoIso(daysAgo) : null,
      revoked_by: status === 'revoked' ? IDS.michel : null,
      revoked_at: status === 'revoked' ? daysAgoIso(15) : null,
      revoke_reason: status === 'revoked' ? 'DEMO. Left the roster August 2026.' : null,
      notes,
      created_at: daysAgoIso(daysAgo + 5),
      updated_at: NOW,
    }
  }

  const qualifications: Qualification[] = [
    qualification(IDS.layla, SVC.birthday, 'approved', 200),
    qualification(IDS.layla, SVC.brunch, 'approved', 190),
    qualification(IDS.layla, SVC.swimming, 'approved', 120),
    qualification(IDS.omar, SVC.birthday, 'approved', 170),
    qualification(IDS.omar, SVC.camp, 'approved', 95),
    qualification(IDS.nadia, SVC.birthday, 'approved', 140),
    qualification(IDS.nadia, SVC.brunch, 'approved', 130),
    qualification(IDS.karim, SVC.birthday, 'approved', 80),
    qualification(IDS.rania, SVC.birthday, 'approved', 310),
    qualification(IDS.rania, SVC.brunch, 'approved', 305),
    qualification(IDS.rania, SVC.camp, 'approved', 85),
    qualification(IDS.rania, SVC.corporate, 'approved', 300),
    qualification(IDS.rania, SVC.swimming, 'approved', 290),
    qualification(IDS.rania, SVC.cdp, 'approved', 195),
    qualification(IDS.youssef, SVC.birthday, 'approved', 210),
    qualification(IDS.youssef, SVC.brunch, 'approved', 200),
    qualification(IDS.youssef, SVC.corporate, 'approved', 55),
    // Waiting on Michel. These are what the approvals queue shows, and every
    // one of them has its module set finished: eligible, not yet qualified.
    qualification(
      IDS.youssef,
      SVC.camp,
      'pending',
      3,
      'DEMO. Camp modules finished. Waiting on the approval call so he can cover Hatta.',
    ),
    qualification(IDS.nadia, SVC.swimming, 'pending', 12),
    qualification(IDS.karim, SVC.brunch, 'pending', 6),
    qualification(
      IDS.hana,
      SVC.birthday,
      'pending',
      2,
      'DEMO. Self-service candidate. Fundamentals done, waiting on the approval call.',
    ),
    // Departed coach, access removed in both directions.
    qualification(IDS.tariq, SVC.birthday, 'revoked', 400),
    qualification(IDS.tariq, SVC.cdp, 'revoked', 380),
  ]

  /* ------------------------------------------------------------------ events */

  interface EventSpec {
    n: number
    zoho: string
    title: string
    service: keyof typeof SVC
    status: EventRow['status']
    offset: number
    time: string
    duration: number
    locationName: string
    address: string
    zone: EventRow['location_zone']
    required: number
    brief: string
    client: string
    contact: string
  }

  const eventSpecs: EventSpec[] = [
    { n: 1, zoho: 'ZCRM-DEAL-1001', title: 'Maya turns 6', service: 'birthday', status: 'completed', offset: -21, time: '16:00', duration: 120, locationName: 'Jumeirah Villa 14', address: 'Jumeirah 3, Dubai', zone: 'dubai', required: 2, brief: 'Unicorn theme. Cake at 17:15. Two siblings, one is shy - keep him included.', client: 'Family Mock', contact: '+971501239001' },
    { n: 2, zoho: 'ZCRM-DEAL-1002', title: 'Summer Camp Day 4', service: 'camp', status: 'completed', offset: -14, time: '09:00', duration: 240, locationName: 'Al Barsha Park', address: 'Al Barsha 2, Dubai', zone: 'dubai', required: 2, brief: 'Ages 7 to 11. Water breaks every 30 minutes. One child has an inhaler.', client: 'Sample School', contact: '+971501239002' },
    { n: 3, zoho: 'ZCRM-DEAL-1003', title: 'Swim session - Mirdif', service: 'swimming', status: 'completed', offset: -10, time: '17:00', duration: 60, locationName: 'Mirdif Community Pool', address: 'Mirdif, Dubai', zone: 'dubai', required: 1, brief: 'Two beginners. Shallow end only.', client: 'Family Demo', contact: '+971501239003' },
    { n: 4, zoho: 'ZCRM-DEAL-1004', title: 'Corporate family day', service: 'corporate', status: 'completed', offset: -7, time: '18:00', duration: 180, locationName: 'DIFC Rooftop', address: 'DIFC, Dubai', zone: 'dubai', required: 2, brief: 'Around 40 kids. Two activity stations plus a closing relay.', client: 'Fixture Group', contact: '+971501239004' },
    { n: 5, zoho: 'ZCRM-DEAL-1005', title: 'Friday brunch kids club', service: 'brunch', status: 'completed', offset: -4, time: '12:00', duration: 180, locationName: 'Palm Resort', address: 'Palm Jumeirah, Dubai', zone: 'dubai', required: 2, brief: 'Rolling attendance. Craft table plus face painting.', client: 'Sandbox Hotels', contact: '+971501239005' },
    { n: 6, zoho: 'ZCRM-DEAL-1006', title: 'Zayed turns 5', service: 'birthday', status: 'staffed', offset: 1, time: '16:30', duration: 120, locationName: 'Arabian Ranches Villa', address: 'Arabian Ranches, Dubai', zone: 'dubai', required: 2, brief: 'Dinosaur theme. Garden setup, bring the shade tent.', client: 'Family Stub', contact: '+971501239006' },
    { n: 7, zoho: 'ZCRM-DEAL-1007', title: 'Twins birthday party', service: 'birthday', status: 'staffed', offset: 3, time: '15:00', duration: 120, locationName: 'Emirates Hills Villa', address: 'Emirates Hills, Dubai', zone: 'dubai', required: 2, brief: 'Twins, 8 years old. 25 guests. Second coach still needed.', client: 'Family Seed', contact: '+971501239007' },
    { n: 8, zoho: 'ZCRM-DEAL-1008', title: 'Autumn Camp Day 1', service: 'camp', status: 'staffed', offset: 5, time: '08:30', duration: 300, locationName: 'Mushrif Park', address: 'Mushrif, Dubai', zone: 'dubai', required: 3, brief: 'Opening day. Registration desk from 08:00. Third coach still needed.', client: 'Sample School', contact: '+971501239002' },
    { n: 9, zoho: 'ZCRM-DEAL-1009', title: 'CDP session - Yasmin', service: 'cdp', status: 'staffed', offset: 6, time: '17:00', duration: 60, locationName: 'La Folie Studio', address: 'Al Quoz, Dubai', zone: 'dubai', required: 1, brief: 'Session 7. Continue the fine-motor sequence from last week.', client: 'Family Dummy', contact: '+971501239008' },
    { n: 10, zoho: 'ZCRM-DEAL-1010', title: 'Corporate team day AD', service: 'corporate', status: 'staffed', offset: 8, time: '19:00', duration: 180, locationName: 'Abu Dhabi Corniche', address: 'Corniche, Abu Dhabi', zone: 'abu_dhabi', required: 2, brief: 'Out of city. Leave Dubai by 17:00. Travel allowance applies.', client: 'Fixture Group', contact: '+971501239004' },
    { n: 11, zoho: 'ZCRM-DEAL-1011', title: 'Swim session - Sharjah', service: 'swimming', status: 'scheduled', offset: 10, time: '16:00', duration: 60, locationName: 'Sharjah Aqua Club', address: 'Al Majaz, Sharjah', zone: 'sharjah', required: 1, brief: 'One beginner, one intermediate.', client: 'Family Mock', contact: '+971501239001' },
    { n: 12, zoho: 'ZCRM-DEAL-1012', title: 'Saturday brunch club', service: 'brunch', status: 'scheduled', offset: 12, time: '12:30', duration: 180, locationName: 'JBR Beach Club', address: 'JBR, Dubai', zone: 'dubai', required: 2, brief: 'Beach setup. Sun cover mandatory.', client: 'Sandbox Hotels', contact: '+971501239005' },
    { n: 13, zoho: 'ZCRM-DEAL-1013', title: 'Layan turns 4', service: 'birthday', status: 'cancelled', offset: 14, time: '17:00', duration: 120, locationName: 'Al Furjan Villa', address: 'Al Furjan, Dubai', zone: 'dubai', required: 1, brief: 'Small party, 12 guests.', client: 'Family Fixture', contact: '+971501239009' },
    { n: 14, zoho: 'ZCRM-DEAL-1014', title: 'Hatta weekend camp', service: 'camp', status: 'scheduled', offset: 20, time: '09:00', duration: 240, locationName: 'Hatta Adventure Base', address: 'Hatta', zone: 'other', required: 4, brief: 'Full-day outdoor camp. Four coaches, two vehicles.', client: 'Sample School', contact: '+971501239002' },
  ]

  const events: EventRow[] = eventSpecs.map((s) => {
    const event_date = dayOffset(s.offset)
    const starts_at = dubaiIso(event_date, s.time)
    return {
      id: EV(s.n),
      zoho_record_id: s.zoho,
      source: 'zoho',
      title: s.title,
      service_id: SVC[s.service],
      status: s.status,
      event_date,
      start_time: `${s.time}:00`,
      duration_minutes: s.duration,
      starts_at,
      ends_at: new Date(new Date(starts_at).getTime() + s.duration * 60_000).toISOString(),
      location_name: s.locationName,
      location_address: s.address,
      location_zone: s.zone,
      coaches_required: s.required,
      brief: s.brief,
      client_name: s.client,
      client_contact: s.contact,
      internal_notes: null,
      cancelled_at: s.status === 'cancelled' ? daysAgoIso(1) : null,
      cancellation_reason:
        s.status === 'cancelled' ? 'DEMO. Client postponed, no replacement date yet.' : null,
      zoho_modified_at: daysAgoIso(2),
      last_synced_at: new Date(Date.now() - HOUR_MS).toISOString(),
      created_at: daysAgoIso(30),
      updated_at: NOW,
    }
  })

  const eventOf = (n: number): EventRow => events[n - 1]
  const serviceOf = (e: EventRow): Service | undefined => services.find((s) => s.id === e.service_id)

  /* ---------------------------------------------------------- availabilities */

  let availSeq = 0
  function availability(
    eventN: number,
    coach_id: string,
    response: Availability['response'],
    note: string | null = null,
    requestedDaysAgo = 4,
  ): Availability {
    availSeq += 1
    return {
      id: `33330000-0000-4000-8000-${String(availSeq).padStart(12, '0')}`,
      event_id: EV(eventN),
      coach_id,
      response,
      responded_at: response === 'pending' ? null : daysAgoIso(requestedDaysAgo - 1),
      note,
      requested_at: daysAgoIso(requestedDaysAgo),
      requested_by: IDS.michel,
      created_at: daysAgoIso(requestedDaysAgo),
      updated_at: NOW,
    }
  }

  const availabilities: Availability[] = [
    // Tomorrow, fully staffed
    availability(6, IDS.layla, 'available', null, 6),
    availability(6, IDS.nadia, 'available', null, 6),
    availability(6, IDS.karim, 'unavailable', 'DEMO. Away that weekend.', 6),
    availability(6, IDS.youssef, 'available', null, 6),
    // Twins party, one seat still open
    availability(7, IDS.layla, 'available'),
    availability(7, IDS.karim, 'pending'),
    availability(7, IDS.nadia, 'tentative', 'DEMO. Can do it if it finishes by 17:00.'),
    availability(7, IDS.youssef, 'pending'),
    // Autumn camp, third coach still needed
    availability(8, IDS.omar, 'available', null, 5),
    availability(8, IDS.rania, 'available', null, 5),
    // CDP: only Rania is qualified
    availability(9, IDS.rania, 'available', null, 3),
    // Abu Dhabi corporate
    availability(10, IDS.rania, 'available', null, 3),
    availability(10, IDS.youssef, 'available', 'DEMO. Happy to drive.', 3),
    // Sharjah swim, requests just went out
    availability(11, IDS.layla, 'pending', null, 1),
    availability(11, IDS.rania, 'pending', null, 1),
    // Saturday brunch
    availability(12, IDS.layla, 'pending', null, 1),
    availability(12, IDS.nadia, 'unavailable', 'DEMO. Family commitment.', 1),
    availability(12, IDS.rania, 'available', null, 1),
    availability(12, IDS.youssef, 'pending', null, 1),
    // The party that then got cancelled
    availability(13, IDS.karim, 'available', null, 2),
    // Hatta camp, four needed and only two camp coaches on the roster
    availability(14, IDS.omar, 'pending', null, 1),
    availability(14, IDS.rania, 'pending', null, 1),
  ]

  /* --------------------------------------------------------------- assignments */

  let assignSeq = 0
  function assignment(
    eventN: number,
    coach_id: string,
    status: Assignment['status'],
    is_lead: boolean,
    assignedDaysAgo: number,
  ): Assignment {
    assignSeq += 1
    const assigned_at = daysAgoIso(assignedDaysAgo)
    return {
      id: `44440000-0000-4000-8000-${String(assignSeq).padStart(12, '0')}`,
      event_id: EV(eventN),
      coach_id,
      status,
      is_lead,
      assigned_at,
      assigned_by: IDS.michel,
      confirmed_at:
        status === 'confirmed' || status === 'completed' ? hoursFromIso(assigned_at, 3) : null,
      cancelled_at: null,
      cancellation_reason: null,
      created_at: assigned_at,
      updated_at: NOW,
    }
  }

  const assignments: Assignment[] = [
    // Past events
    assignment(1, IDS.layla, 'completed', true, 24),
    assignment(1, IDS.karim, 'completed', false, 24),
    assignment(2, IDS.omar, 'completed', true, 18),
    assignment(2, IDS.rania, 'completed', false, 18),
    assignment(3, IDS.layla, 'completed', true, 13),
    assignment(4, IDS.rania, 'completed', true, 11),
    assignment(4, IDS.youssef, 'completed', false, 11),
    assignment(5, IDS.layla, 'completed', true, 8),
    assignment(5, IDS.nadia, 'completed', false, 8),
    // Upcoming
    assignment(6, IDS.layla, 'confirmed', true, 5),
    assignment(6, IDS.nadia, 'assigned', false, 5),
    assignment(7, IDS.layla, 'assigned', true, 3),
    assignment(8, IDS.omar, 'confirmed', true, 4),
    assignment(8, IDS.rania, 'assigned', false, 4),
    assignment(9, IDS.rania, 'confirmed', true, 2),
    assignment(10, IDS.rania, 'assigned', true, 2),
    assignment(10, IDS.youssef, 'assigned', false, 2),
    // On the party that was then cancelled
    assignment(13, IDS.karim, 'assigned', true, 2),
  ]

  const assignmentFor = (eventN: number, coachId: string): Assignment | undefined =>
    assignments.find((a) => a.event_id === EV(eventN) && a.coach_id === coachId)

  /* ------------------------------------------------------------- materials */

  const CHECKLIST_BY_SERVICE: Record<string, Array<[string, number]>> = {
    [SVC.birthday]: [
      ['Party tent', 1],
      ['Bluetooth speaker', 1],
      ['Face paint kit', 1],
      ['Balloon pump', 1],
      ['Prize box', 1],
    ],
    [SVC.brunch]: [
      ['Craft table kit', 1],
      ['Colouring packs', 30],
      ['Bluetooth speaker', 1],
    ],
    [SVC.camp]: [
      ['First aid kit', 1],
      ['Cone set', 10],
      ['Footballs', 4],
      ['Water cooler', 1],
      ['Attendance clipboards', 2],
    ],
    [SVC.corporate]: [
      ['PA system', 1],
      ['Team bibs', 20],
      ['Stopwatch', 1],
    ],
    [SVC.swimming]: [
      ['Rescue tube', 1],
      ['Kickboards', 6],
      ['Whistle', 1],
    ],
    [SVC.cdp]: [
      ['Sensory kit', 1],
      ['Observation tablet', 1],
    ],
  }

  const materialsChecks: MaterialsCheck[] = []
  const checklistItems: ChecklistItem[] = []
  let checkSeq = 0
  let itemSeq = 0

  for (const ev of events) {
    const template = CHECKLIST_BY_SERVICE[ev.service_id] ?? []
    const isPast = new Date(ev.starts_at).getTime() < Date.now()
    for (const phase of ['check_out', 'check_in'] as const) {
      checkSeq += 1
      const checkId = `55550000-0000-4000-8000-${String(checkSeq).padStart(12, '0')}`
      // Past events are signed off on both phases, except the Friday brunch
      // crate: it is still out with Layla, owed back along with her report.
      // Tomorrow's party has not been collected yet, so the coach ticks that
      // list off in the demo. Those two are the live materials story.
      const signedOff = isPast && !(ev.id === EV(5) && phase === 'check_in')
      materialsChecks.push({
        id: checkId,
        event_id: ev.id,
        phase,
        status: signedOff ? 'complete' : 'pending',
        checked_by: signedOff ? IDS.gina : null,
        checked_at: signedOff
          ? phase === 'check_out'
            ? hoursFromIso(ev.starts_at, -3)
            : hoursFromIso(ev.ends_at, 1)
          : null,
        notes: null,
        created_at: daysAgoIso(30),
        updated_at: NOW,
      })
      template.forEach(([label, quantity], i) => {
        itemSeq += 1
        checklistItems.push({
          id: `66660000-0000-4000-8000-${String(itemSeq).padStart(12, '0')}`,
          materials_check_id: checkId,
          label,
          quantity,
          state: signedOff ? 'ok' : 'pending',
          note: null,
          sort_order: i + 1,
          updated_by: signedOff ? IDS.gina : null,
          created_at: daysAgoIso(30),
          updated_at: NOW,
        })
      })
    }
  }

  // One unresolved discrepancy, so the materials screen has a real issue to show.
  const swimCheckIn = materialsChecks.find(
    (m) => m.event_id === EV(3) && m.phase === 'check_in',
  )
  if (swimCheckIn) {
    swimCheckIn.status = 'issue'
    swimCheckIn.notes = 'DEMO. One kickboard did not come back.'
    const kickboards = checklistItems.find(
      (i) => i.materials_check_id === swimCheckIn.id && i.label === 'Kickboards',
    )
    if (kickboards) {
      kickboards.state = 'missing'
      kickboards.note = 'DEMO. Not returned.'
    }
  }

  /* ------------------------------------------------------ post-event reports */

  // Deliberately sparse. Layla still owes reports on the Mirdif swim and the
  // Friday brunch, which is what the "report due" prompt is built to catch.
  const postEventReports: PostEventReport[] = [
    {
      id: '77770000-0000-4000-8000-000000000001',
      event_id: EV(1),
      coach_id: IDS.layla,
      assignment_id: assignmentFor(1, IDS.layla)?.id ?? null,
      submitted_at: daysAgoIso(21),
      overall_rating: 5,
      attendance_count: 18,
      went_well: 'DEMO. Unicorn theme landed well, the shy sibling joined in by the second game.',
      issues: null,
      incident: false,
      incident_details: null,
      client_feedback: 'DEMO. Parents asked for the same team next year.',
      photo_paths: [],
      created_at: daysAgoIso(21),
      updated_at: daysAgoIso(21),
    },
    {
      id: '77770000-0000-4000-8000-000000000002',
      event_id: EV(2),
      coach_id: IDS.omar,
      assignment_id: assignmentFor(2, IDS.omar)?.id ?? null,
      submitted_at: daysAgoIso(14),
      overall_rating: 4,
      attendance_count: 24,
      went_well: 'DEMO. Good energy, water breaks kept everyone comfortable.',
      issues: 'DEMO. Shade at the far pitch was not enough after 11:00.',
      incident: false,
      incident_details: null,
      client_feedback: null,
      photo_paths: [],
      created_at: daysAgoIso(14),
      updated_at: daysAgoIso(14),
    },
  ]

  /* ------------------------------------------------------------- pay rates */

  // PLACEHOLDER GRID, generated exactly the way seed.sql section 7 does:
  // service base/hourly x seniority multiplier x zone travel allowance.
  // Every row carries is_placeholder = true and nothing here is coach-visible.
  const RATE_BY_SERVICE: Record<string, [number, number]> = {
    birthday: [50, 60],
    brunch: [50, 55],
    camp: [75, 65],
    corporate: [75, 80],
    swimming: [40, 70],
    cdp: [60, 90],
  }
  const SENIORITY_MULTIPLIER: Array<[PayRate['seniority'], number]> = [
    ['junior', 1.0],
    ['standard', 1.2],
    ['senior', 1.5],
    ['lead', 1.8],
  ]
  const ZONE_TRAVEL: Array<[PayRate['location_zone'], number]> = [
    ['dubai', 0],
    ['sharjah', 40],
    ['abu_dhabi', 60],
    ['northern_emirates', 80],
    ['other', 50],
  ]

  const payRates: PayRate[] = []
  let rateSeq = 0
  for (const service of services) {
    const rate = RATE_BY_SERVICE[service.code]
    if (!rate) continue
    for (const [seniority, mult] of SENIORITY_MULTIPLIER) {
      for (const [zone, travel] of ZONE_TRAVEL) {
        rateSeq += 1
        payRates.push({
          id: `99990000-0000-4000-8000-${String(rateSeq).padStart(12, '0')}`,
          service_id: service.id,
          seniority,
          location_zone: zone,
          min_duration_minutes: 0,
          max_duration_minutes: 1440,
          base_amount_aed: Math.round(rate[0] * mult * 100) / 100,
          hourly_rate_aed: Math.round(rate[1] * mult * 100) / 100,
          travel_allowance_aed: travel,
          currency: 'AED',
          effective_from: '2026-01-01',
          effective_to: null,
          is_placeholder: true,
          notes: 'PLACEHOLDER. Replace from the Drive pay-rate grid once Pascal signs off.',
          ...stamps,
        })
      }
    }
  }

  /* --------------------------------------------------------------- earnings */

  // One per assignment, computed by the same math as the SQL trigger, never
  // typed in by hand. is_visible_to_coach is false on every row and stays that
  // way until Pascal signs the grid off and a shadow month reconciles.
  const earnings: Earning[] = assignments.map((a, i) => {
    const ev = events.find((e) => e.id === a.event_id)!
    const coach = profiles.find((p) => p.id === a.coach_id)!
    const isVoid =
      a.status === 'cancelled' ||
      a.status === 'no_show' ||
      ev.status === 'cancelled' ||
      ev.cancelled_at !== null
    const breakdown = calculateEventPay(payRates, {
      serviceId: ev.service_id,
      seniority: coach.seniority,
      locationZone: ev.location_zone,
      durationMinutes: ev.duration_minutes,
      onDate: ev.event_date,
    })
    const isPast = new Date(ev.starts_at).getTime() < Date.now()
    const status: Earning['status'] = isVoid ? 'void' : isPast ? 'approved' : 'draft'
    return {
      id: `88880000-0000-4000-8000-${String(i + 1).padStart(12, '0')}`,
      assignment_id: a.id,
      coach_id: a.coach_id,
      event_id: a.event_id,
      pay_rate_id: breakdown.pay_rate_id,
      amount_aed: isVoid ? 0 : breakdown.amount_aed,
      currency: 'AED',
      breakdown,
      status,
      period_month: dubaiPeriodMonth(ev.starts_at),
      computed_at: a.assigned_at,
      approved_by: status === 'approved' ? IDS.pascal : null,
      approved_at: status === 'approved' ? daysAgoIso(2) : null,
      paid_at: null,
      payout_reference: null,
      is_visible_to_coach: false,
      notes:
        status === 'approved' ? 'DEMO. Approved on placeholder rates, not payable.' : null,
      created_at: a.assigned_at,
      updated_at: NOW,
    }
  })

  /* -------------------------------------------------------------- sanctions */

  const sanctions: Sanction[] = [
    {
      id: 'aaaa1111-0000-4000-8000-000000000001',
      coach_id: IDS.layla,
      type: 'warning_1',
      reason: 'DEMO. Cancelled 9 hours before a birthday party.',
      contract_clause: '4.3',
      event_id: null,
      amount_aed: 0,
      issued_by: IDS.michel,
      issued_at: daysAgoIso(45),
      is_revoked: false,
      revoked_by: null,
      revoked_at: null,
      revoke_reason: null,
      acknowledged_at: null,
      created_at: daysAgoIso(45),
      updated_at: NOW,
    },
    {
      id: 'aaaa1111-0000-4000-8000-000000000002',
      coach_id: IDS.tariq,
      type: 'warning_2',
      reason: 'DEMO. Second late cancellation within 12 hours.',
      contract_clause: '4.3',
      event_id: null,
      amount_aed: 0,
      issued_by: IDS.michel,
      issued_at: daysAgoIso(30),
      is_revoked: true,
      revoked_by: IDS.pascal,
      revoked_at: daysAgoIso(20),
      revoke_reason: 'DEMO. Documented medical reason accepted by Pascal.',
      acknowledged_at: daysAgoIso(19),
      created_at: daysAgoIso(30),
      updated_at: NOW,
    },
  ]

  /* ----------------------------------------------------- notification queue */

  // Generated, not typed out: every assignment runs through the same enqueue
  // contract the SQL trigger uses, as if the trigger had fired when the
  // assignment was created. Then the dispatcher is simulated (anything due has
  // been sent), and finally a small, deliberate set is left unacknowledged so
  // the chase list has something real in it.
  const queued: NewNotification[] = []
  for (const a of assignments) {
    const ev = events.find((e) => e.id === a.event_id)!
    queued.push(...buildAssignmentNotifications(a, ev, serviceOf(ev), a.assigned_at))
  }

  // The party that was cancelled: reminders that had not gone out yet die, and
  // the assigned coach is told once. Mirrors public.events_after_update().
  const cancelledEvent = eventOf(13)
  const cancelledAt = cancelledEvent.cancelled_at ?? NOW
  const cancelledAtMs = new Date(cancelledAt).getTime()
  for (const a of assignments.filter((x) => x.event_id === cancelledEvent.id)) {
    for (const row of queued) {
      if (row.assignment_id === a.id && new Date(row.send_at).getTime() > cancelledAtMs) {
        row.status = 'cancelled'
      }
    }
    queued.push(buildEventCancelledNotification(a, cancelledEvent, cancelledAt))
  }

  // Availability requests are written by the app, not by a trigger. Everyone
  // who was asked got one.
  for (const av of availabilities) {
    const ev = events.find((e) => e.id === av.event_id)!
    queued.push(
      buildAvailabilityRequestNotification(av.coach_id, ev, serviceOf(ev), av.requested_at),
    )
  }

  // Karim's most recent unlock, so the coach inbox shows what an approval feels like.
  const karimBirthday = qualifications.find(
    (q) => q.coach_id === IDS.karim && q.service_id === SVC.birthday,
  )
  const birthdayService = services.find((s) => s.id === SVC.birthday)!
  if (karimBirthday?.approved_at) {
    queued.push(
      buildQualificationApprovedNotification(
        IDS.karim,
        karimBirthday.id,
        birthdayService,
        karimBirthday.approved_at,
      ),
    )
  }

  // What nobody has tapped. Two deliberate groups:
  //  - an availability request the coach has not answered yet
  //  - everything about tomorrow's party, so Michel has a live chase list on an
  //    event that is actually imminent
  const unacknowledgedKeys = new Set<string>()
  for (const av of availabilities) {
    if (av.response === 'pending') {
      unacknowledgedKeys.add(`availability:${av.event_id}:${av.coach_id}:availability_request`)
    }
  }
  for (const row of queued) {
    if (row.event_id === EV(6) && row.type !== 'availability_request') {
      unacknowledgedKeys.add(row.dedupe_key)
    }
  }

  // Simulate the dispatcher: anything already due has gone out.
  const nowMs = Date.now()
  let notifSeq = 0
  const notificationQueue: NotificationQueueRow[] = queued.map((row) => {
    notifSeq += 1
    const sent = row.status === 'pending' && new Date(row.send_at).getTime() <= nowMs
    return {
      ...row,
      id: `bbbb1111-0000-4000-8000-${String(notifSeq).padStart(12, '0')}`,
      status: sent ? 'sent' : row.status,
      attempts: sent ? 1 : 0,
      sent_at: sent ? row.send_at : null,
      acknowledged_at:
        sent && !unacknowledgedKeys.has(row.dedupe_key) ? hoursFromIso(row.send_at, 0.5) : null,
      created_at: row.send_at,
    }
  })

  /* ------------------------------------------------------ push subscriptions */

  const pushSubscriptions: PushSubscriptionRow[] = [
    {
      id: 'cccc1111-0000-4000-8000-000000000001',
      coach_id: IDS.layla,
      endpoint: 'https://push.example.invalid/lafolie/demo-endpoint-layla',
      p256dh: 'DEMO-p256dh-key-layla',
      auth: 'DEMO-auth-layla',
      user_agent: 'Mozilla/5.0 (iPhone) Safari',
      platform: 'ios',
      is_active: true,
      failure_count: 0,
      last_success_at: new Date(Date.now() - 2 * HOUR_MS).toISOString(),
      last_failure_at: null,
      created_at: daysAgoIso(20),
      updated_at: NOW,
    },
    {
      id: 'cccc1111-0000-4000-8000-000000000002',
      coach_id: IDS.omar,
      endpoint: 'https://push.example.invalid/lafolie/demo-endpoint-omar',
      p256dh: 'DEMO-p256dh-key-omar',
      auth: 'DEMO-auth-omar',
      user_agent: 'Mozilla/5.0 (Android) Chrome',
      platform: 'android',
      is_active: true,
      failure_count: 0,
      last_success_at: new Date(Date.now() - 2 * HOUR_MS).toISOString(),
      last_failure_at: null,
      created_at: daysAgoIso(18),
      updated_at: NOW,
    },
  ]

  return {
    services,
    trainingModules,
    serviceModuleRequirements,
    profiles,
    moduleCompletions,
    qualifications,
    events,
    availabilities,
    assignments,
    materialsChecks,
    checklistItems,
    postEventReports,
    payRates,
    earnings,
    sanctions,
    notificationQueue,
    pushSubscriptions,
  }
}

/**
 * The live demo database.
 *
 * Its contents are mutable and are owned by mock/store.ts, which hydrates it
 * from sessionStorage, writes back after every mutation, and can restore it to
 * a fresh seed. Read it through store.tables() so hydration is guaranteed to
 * have happened; the one direct reader is AuthProvider, which only ever looks
 * at profiles, and profiles never change in demo mode.
 */
export const demoDb: DemoTables = createSeedData()

/** Demo password, matching seed.sql. */
export const DEMO_PASSWORD = 'lafolie2026'
